'use client'

import { toCMSPage } from '@/feature/cms/services/cms.routing'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { INTENT_SAME_AS_WEB } from '@/lib/react-native/services/react-native.intent'
import { Button } from '@/ui/buttons/button/Button.client'
import { Card } from '@/ui/cards/card/Card'
import { FullImageCard } from '@/ui/cards/full-image-card/FullImageCard'
import { Drawer, DrawerBody } from '@/ui/drawer/drawer/Drawer.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { Container } from '@/ui/layouts/container/Container'
import { InlineImageLink } from '@/ui/links/inline-image-link/InlineImageLink.client'
import { Link } from '@/ui/links/link/Link.client'
import { generatePictureSource } from '@/ui/media/picture/Picture.service'
import { Skin } from '@/ui/skin/Skin'
import { Title } from '@/ui/text/title/Title'
import { gql } from '@gql-generated/gql'
import type { HubList_CategoriesFragment } from '@gql-generated/graphql'
import { Fragment, useEffect, useState } from 'react'
import type { HubListType } from './HubList.schema'

gql(/* GraphQL */ `
  fragment HubList_Categories on Category {
    code
    name
    mainImage {
      ...FullPicture
    }
  }
`)

type HubListProps = {
  list: HubListType
  categories: HubList_CategoriesFragment[]
}

export function HubList({ list, categories }: HubListProps): React.ReactElement {
  const { t } = useTranslation(['hub'])

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [filterByCategory, setFilterByCategory] = useState('')

  // Filtering resizes the results grid while it's on screen, which makes Chrome's
  // scroll anchoring pick a wrong anchor and jump the page; only overflow-anchor:none
  // on <body> (not on any descendant) reliably prevents it, so toggle it around the update
  const handleCategoryClick = (category: string) => {
    document.body.style.overflowAnchor = 'none'

    if (filterByCategory === category) {
      setFilterByCategory('')
    } else {
      setFilterByCategory(category)
    }

    setIsDrawerOpen(false)
  }

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      document.body.style.overflowAnchor = ''
    })

    return () => cancelAnimationFrame(raf)
  }, [filterByCategory])

  /** Object mapping category IDs to their corresponding category fragments. */
  const categoryNames = categories.reduce<Record<string, HubList_CategoriesFragment>>(
    (acc, category) => {
      if (category.code && category.name) {
        acc[category.code] = category
      }

      return acc
    },
    {},
  )

  /** List of categories derived from the pages in the list, ensuring uniqueness. */
  const categoriesFromList = list.reduce<{ code: string; name: string }[]>((acc, page) => {
    if (!acc.find((cat) => cat.code === page.category)) {
      acc.push({ code: page.category, name: categoryNames[page.category]?.name || '' })
    }

    return acc
  }, [])

  /** The total of pages in each category. */
  const categoriesCounts = list.reduce<Record<string, number>>((acc, page) => {
    acc[page.category] = (acc[page.category] || 0) + 1

    return acc
  }, {})

  const filteredList = filterByCategory
    ? list.filter((page) => page.category === filterByCategory)
    : list

  const categoryList = (containerClassName?: string) => (
    <div className="flex flex-col gap-y-1">
      {categoriesFromList.map((category) => (
        <InlineImageLink
          key={category.code}
          onPress={() => handleCategoryClick(category.code)}
          className={twMerge(category.code === filterByCategory && '!font-bold text-primary')}
          containerClassName={containerClassName}
          image={categoryNames[category.code]?.mainImage || undefined}
        >
          {category.name} ({categoriesCounts[category.code]})
        </InlineImageLink>
      ))}
    </div>
  )

  return (
    <>
      <Container className="mb-3 mt-1 desktop:hidden">
        <Button
          size="md"
          className="w-full"
          variant="secondary"
          iconLeft={<Icon.ListFilter />}
          onPress={() => setIsDrawerOpen(true)}
        >
          {t('hub:filterTitle')}
        </Button>
      </Container>
      <Skin tint="body-alt" className="desktop:mt-1">
        <Container className="py-4">
          <div className="flex flex-col gap-4 desktop:flex-row">
            <div className="hidden w-full desktop:block desktop:w-[18.75rem]">
              <Card className="flex flex-1 flex-col gap-y-3 px-0 py-3" radius="sm">
                <Title as="h2" className="px-3 font-bold uppercase">
                  {t('hub:filterTitle')}
                </Title>
                {categoryList('!px-3')}
              </Card>
            </div>
            <div className="grid flex-1 grid-cols-1 gap-2 tablet:grid-cols-3 desktop:grid-cols-2 desktop-md:grid-cols-3">
              {filteredList.map((page, index) => (
                <Fragment key={page.urlKey}>
                  <Link
                    variant="free"
                    className="group inline"
                    href={toCMSPage(page.urlKey)}
                    intent={INTENT_SAME_AS_WEB}
                  >
                    <FullImageCard
                      index={index}
                      title={page.title}
                      subtitle={categoryNames[page.category]?.name}
                      size="auto"
                      image={generatePictureSource({
                        mobileUrl: page.picturePath,
                        alt: page.title,
                      })}
                    />
                  </Link>
                </Fragment>
              ))}
            </div>
          </div>
        </Container>
      </Skin>
      <Drawer title={t('hub:filterTitle')} isOpen={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerBody>
          <div className={twMerge(!isDrawerOpen && 'pointer-events-none')}>{categoryList()}</div>
        </DrawerBody>
      </Drawer>
    </>
  )
}
